import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { samplesAPI } from '../../api';
import { Loading } from '../../components';
import './Dashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await samplesAPI.getStats();
        setStats(response.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) return <Loading message="Loading dashboard..." />;
  if (error) return <div className="alert alert-error">{error}</div>;

  // Calculate testing count (field tested + lab tested but not published)
  const testingCount = (stats.byStatus?.fieldTestedOnly || 0) + (stats.byStatus?.labTested || 0);
  const publishedCount = stats.byStatus?.published || 0;

  const lifecycleData = {
    labels: ['Testing', 'Published'],
    datasets: [
      {
        data: [testingCount, publishedCount],
        backgroundColor: ['rgba(251, 191, 36, 0.8)', 'rgba(74, 222, 128, 0.8)'],
        borderColor: ['#fbbf24', '#4ade80'],
        borderWidth: 2
      }
    ]
  };

  const overallData = {
    labels: ['Acceptable', 'Not Acceptable'],
    datasets: [
      {
        label: 'Sample Quality',
        data: [
          stats.byOverallStatus?.ACCEPTABLE || 0,
          stats.byOverallStatus?.NOT_ACCEPTABLE || 0
        ],
        backgroundColor: ['rgba(74, 222, 128, 0.8)', 'rgba(248, 113, 113, 0.8)'],
        borderColor: ['#4ade80', '#f87171'],
        borderWidth: 2
      }
    ]
  };

  const lifecycleChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 12, weight: '500' }
        }
      },
      tooltip: {
        callbacks: {
          afterBody: () => 'Click to view samples'
        }
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const idx = elements[0].index;
        // 0 = Testing, 1 = Published
        if (idx === 1) {
          navigate('/samples?status=published');
        } else {
          navigate('/samples?status=field_only');
        }
      } else {
        navigate('/samples');
      }
    }
  };

  const qualityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 12, weight: '500' }
        }
      },
      tooltip: {
        callbacks: {
          afterBody: () => 'Click to view reports'
        }
      }
    },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        // Navigate to samples page filtered by published status to access reports
        navigate('/samples?status=published');
      } else {
        navigate('/samples');
      }
    }
  };

  return (
    <div className="dashboard admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p>Earth Warrior - Water Quality Management Overview</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-total" onClick={() => navigate('/samples')} style={{ cursor: 'pointer' }} title="View all samples">
          <div className="stat-icon-wrapper total">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Samples</span>
            <span className="stat-number">{stats.total}</span>
          </div>
        </div>

        <div className="stat-card stat-testing" onClick={() => navigate('/samples')} style={{ cursor: 'pointer' }} title="View samples in testing">
          <div className="stat-icon-wrapper testing">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">In Testing</span>
            <span className="stat-number">{testingCount}</span>
          </div>
        </div>

        <div className="stat-card stat-published" onClick={() => navigate('/samples')} style={{ cursor: 'pointer' }} title="View published samples">
          <div className="stat-icon-wrapper published">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Published</span>
            <span className="stat-number">{publishedCount}</span>
          </div>
        </div>

        <div className="stat-card stat-archived" onClick={() => navigate('/samples')} style={{ cursor: 'pointer' }} title="View archived samples">
          <div className="stat-icon-wrapper archived">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Archived</span>
            <span className="stat-number">{stats.archived || 0}</span>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3>Lifecycle Status Distribution</h3>
          <p className="chart-desc">Distribution by testing status</p>
          <div className="chart-container">
            <Doughnut data={lifecycleData} options={lifecycleChartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <h3>Overall Quality Distribution</h3>
          <p className="chart-desc">Sample quality breakdown</p>
          <div className="chart-container">
            <Bar data={overallData} options={qualityChartOptions} />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <Link to="/samples" className="action-card">
            <div className="action-icon-wrapper samples">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <span className="action-label">Manage Samples</span>
          </Link>
          <Link to="/parameters" className="action-card">
            <div className="action-icon-wrapper params">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="action-label">Parameters</span>
          </Link>
          <Link to="/team" className="action-card">
            <div className="action-icon-wrapper team">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <span className="action-label">Team Members</span>
          </Link>
          <Link to="/public" className="action-card">
            <div className="action-icon-wrapper portal">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="action-label">Public Portal</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
